import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Button,
  Grid,
  TextField,
  Divider,
  Card,
  CardContent,
  Paper,
} from '@mui/material';
import { CameraService } from '../../../service/camera.service';
import { DashboardService } from '../../../service/dashboard.service';
// ENTFERNT: import { SettingsDeploymentService } from '../../../service/settings-deployment.service';
import KameraItem from '../../../components/KameraItem';
import AddIcon from '@mui/icons-material/Add';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import { useSettingsContext } from '../../../context/SettingsContext';

// Typdefinitionen
interface Kamera {
  ref: string;
  displayName: string;
  url: string;
  abstandsschwelle: number;
  websocketOff: boolean;
  rtspUser?: string;
  rtspPwd?: string;
  plainUrl?: string;
}

interface UrlComponents {
  protocol: string;
  username: string;
  password: string;
  ip: string;
  port: string;
  path: string;
}

// Vereinfachte Version der URL-Komponenten
const emptyUrlComponents: UrlComponents = {
  protocol: 'rtsp://', // Fest vorgegeben
  username: '',
  password: '',
  ip: '',
  port: '554',
  path: '/Streaming/Channels/102', 
};

// Funktion zum Zusammenbauen der URL
const buildUrl = (components: UrlComponents): string => {
  // Only add authentication part if both username and password are provided
  const auth = components.username && components.password 
    ? `${components.username}:${components.password}@` 
    : '';
  const port = components.port ? `:${components.port}` : '';
  return `${components.protocol}${auth}${components.ip}${port}${components.path}`;
};

// Funktion zum Erstellen der Plain-URL ohne Authentifizierung
const buildPlainUrl = (components: UrlComponents): string => {
  const port = components.port ? `:${components.port}` : '';
  return `${components.protocol}${components.ip}${port}${components.path}`;
};

// Funktion zum Zerlegen der URL
const parseUrl = (url: string): UrlComponents => {
  try {
    const result = { ...emptyUrlComponents };
    
    // Wenn die URL leer ist, gib leere Komponenten zurück
    if (!url) {
      return result;
    }
    
    // Extract protocol
    const protocolMatch = url.match(/^(.*?:\/\/)/);
    if (protocolMatch) {
      result.protocol = protocolMatch[1];
      url = url.substring(protocolMatch[1].length);
    }
    
    // Extract auth if present
    const authMatch = url.match(/^([^:@]+):([^@]+)@/);
    if (authMatch) {
      result.username = authMatch[1] || '';
      result.password = authMatch[2] || '';
      url = url.substring(authMatch[0].length);
    }
    
    // Extract IP and port
    const ipPortMatch = url.match(/^([^:/]+)(?::(\d+))?(\/.+)?$/);
    if (ipPortMatch) {
      result.ip = ipPortMatch[1] || '';
      result.port = ipPortMatch[2] || '554'; // Default port if not specified
      result.path = ipPortMatch[3] || '/Streaming/Channels/101'; // Default path if not specified
    }
    
    console.log('Parsed URL Components:', JSON.stringify(result, null, 2));
    return result;
  } catch (error) {
    console.error('Error parsing URL:', error);
    return emptyUrlComponents;
  }
};

const CameraSettings: React.FC = () => {
  // State Definitionen
  const [kameras, setKameras] = useState<Kamera[]>([]);
  const [addingKamera, setAddingKamera] = useState(false);
  const [newKamera, setNewKamera] = useState<Kamera>({
    ref: '',
    displayName: '',
    url: '',
    abstandsschwelle: 0.1,
    websocketOff: true,
    rtspUser: '',
    rtspPwd: '',
    plainUrl: '',
  });
  const [urlComponents, setUrlComponents] = useState<UrlComponents>(emptyUrlComponents);
  
  // Settings Context für Change Tracking
  const { markAsChanged } = useSettingsContext();

  // Effekte & Datenladung
  useEffect(() => {
    fetchCameras();
  }, []);

  useEffect(() => {
    if (addingKamera && newKamera.url) {
      setUrlComponents(parseUrl(newKamera.url));
    } else if (addingKamera) {
      setUrlComponents(emptyUrlComponents);
    }
  }, [addingKamera, newKamera.url]);

  const updateMissingCameraData = async (camera: Kamera) => {
    try {
      // Überprüfe, ob Daten fehlen
      if (!camera.rtspUser || !camera.rtspPwd || !camera.plainUrl) {
        console.log(`Updating missing data for camera: ${camera.displayName}`);
        
        // Parse URL und extrahiere Daten
        const parsedComponents = parseUrl(camera.url);
        const updatedData: Partial<Kamera> = {};
        
        // Nur fehlende Werte aktualisieren
        if (!camera.rtspUser && parsedComponents.username) {
          updatedData.rtspUser = parsedComponents.username;
        }
        
        if (!camera.rtspPwd && parsedComponents.password) {
          updatedData.rtspPwd = parsedComponents.password;
        }
        
        if (!camera.plainUrl) {
          updatedData.plainUrl = buildPlainUrl(parsedComponents);
        }
        
        // Nur senden, wenn es Aktualisierungen gibt
        if (Object.keys(updatedData).length > 0) {
          console.log(`Sending updates to backend for camera ${camera.ref}:`, updatedData);
          await CameraService.updateCamera(camera.ref, updatedData);
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('Fehler beim Aktualisieren der fehlenden Kameradaten:', error);
      return false;
    }
  };

  const fetchCameras = async () => {
    try {
      const data = await DashboardService.getDashboard();
      console.log("Camera data loaded from backend:", data);

      if (data.cameras) {
        // Erweiterte Logging für jede Kamera
        data.cameras.forEach((camera: any, index: number) => {
          console.log(`Camera ${index + 1} (${camera.displayName}) data:`, {
            ref: camera.ref,
            url: camera.url,
            rtspUser: camera.rtspUser || 'FEHLT',
            rtspPwd: camera.rtspPwd || 'FEHLT',
            plainUrl: camera.plainUrl || 'FEHLT'
          });
        });
        
        // Fülle fehlende Daten aus der URL, falls notwendig
        const processedCameras = data.cameras.map((k: any) => {
          const camera = {
            ref: k.ref,
            displayName: k.displayName,
            url: k.url,
            abstandsschwelle: k.abstandsschwelle || 0.1,
            websocketOff: k.websocketOff ?? true,
            rtspUser: k.rtspUser || '',
            rtspPwd: k.rtspPwd || '',
            plainUrl: k.plainUrl || '',
          };
          
          // Wenn rtspUser, rtspPwd oder plainUrl fehlen, versuche sie aus der URL zu extrahieren
          if (!k.rtspUser || !k.rtspPwd || !k.plainUrl) {
            console.log(`Regenerating missing data for camera: ${k.displayName}`);
            const parsedComponents = parseUrl(k.url);
            
            // Nur fehlende Werte überschreiben
            if (!camera.rtspUser && parsedComponents.username) {
              camera.rtspUser = parsedComponents.username;
            }
            
            if (!camera.rtspPwd && parsedComponents.password) {
              camera.rtspPwd = parsedComponents.password;
            }
            
            if (!camera.plainUrl) {
              camera.plainUrl = buildPlainUrl(parsedComponents);
            }
            
            // Aktualisiere die Kamera im Backend
            setTimeout(() => {
              updateMissingCameraData(camera);
            }, 500);
          }
          
          return camera;
        });
        
        setKameras(processedCameras);
      }
    } catch (error) {
      console.error('Fehler beim Abrufen der Kamera-Daten:', error);
    }
  };

  // URL zusammenbauen beim Ändern von Komponenten
  const handleComponentChange = (field: keyof UrlComponents, value: string) => {
    const updatedComponents = { ...urlComponents, [field]: value };
    setUrlComponents(updatedComponents);
    
    // Aktualisiere auch das newKamera-Objekt mit der neuen URL
    const newUrl = buildUrl(updatedComponents);
    const plainUrl = buildPlainUrl(updatedComponents);
    
    setNewKamera({ 
      ...newKamera, 
      url: newUrl,
      plainUrl: plainUrl,
      rtspUser: updatedComponents.username,
      rtspPwd: updatedComponents.password,
      abstandsschwelle: 0.1 // Standard-Abstandsschwelle setzen
    });
    
    console.log('Updated newKamera:', {
      url: newUrl,
      plainUrl,
      rtspUser: updatedComponents.username,
      rtspPwd: updatedComponents.password
    });
  };

  // Prüfe, ob das Formular gültig ist
  const isFormValid = () => {
    return (
      !!newKamera.displayName && 
      !!urlComponents.ip && 
      !!urlComponents.username &&
      !!urlComponents.password
    );
  };

  const handleConfirmAddKamera = async () => {
    if (!isFormValid()) return;
    
    try {
      const response = await CameraService.addCamera({
        displayName: newKamera.displayName,
        url: newKamera.url,
        abstandsschwelle: newKamera.abstandsschwelle,
        websocketOff: newKamera.websocketOff,
        rtspUser: newKamera.rtspUser,
        rtspPwd: newKamera.rtspPwd,
        plainUrl: newKamera.plainUrl,
      });
      
      console.log('Kamera hinzugefügt:', response);
      
      // Markiere als geändert für Deployment-Button
      markAsChanged();
      
      // Update lokal die Kamera-Liste
      await fetchCameras();
      
      // Reset form
      setNewKamera({
        ref: '',
        displayName: '',
        url: '',
        abstandsschwelle: 0.1,
        websocketOff: true,
        rtspUser: '',
        rtspPwd: '',
        plainUrl: '',
      });
      setUrlComponents(emptyUrlComponents);
      setAddingKamera(false);
      
      // Kein automatisches Deployment - User muss "Änderungen speichern" klicken
    } catch (error) {
      console.error('Fehler beim Hinzufügen der Kamera:', error);
    }
  };

  const handleCameraUpdate = async (index: number, updatedData: Partial<Kamera>) => {
    try {
      const kamera = kameras[index];
      const updatedKamera = { ...kamera, ...updatedData };
      
      // Update camera via API
      await CameraService.updateCamera(kamera.ref, updatedData);
      
      // Markiere als geändert für Deployment-Button
      markAsChanged();
      
      // Update local state
      const updatedKameras = [...kameras];
      updatedKameras[index] = updatedKamera;
      setKameras(updatedKameras);
      
      // Kein automatisches Deployment - User muss "Änderungen speichern" klicken
    } catch (error) {
      console.error('Fehler beim Aktualisieren der Kamera:', error);
    }
  };



  const handleCameraDelete = async (index: number) => {
    try {
      const cameraToDelete = kameras[index];
      await CameraService.deleteCamera(cameraToDelete.ref);
      
      // Markiere als geändert für Deployment-Button
      markAsChanged();
      
      // Kameras-Array aktualisieren
      const updatedKameras = kameras.filter((_, idx) => idx !== index);
      setKameras(updatedKameras);
    } catch (error) {
      console.error('Fehler beim Löschen der Kamera:', error);
      alert('Fehler beim Löschen der Kamera. Bitte versuchen Sie es erneut.');
    }
  };

  return (
    <Box sx={{ maxWidth: '100%' }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
        <CameraAltIcon sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h5" component="h1">
          Kamera-Einstellungen
        </Typography>
      </Box>

      <Card sx={{ mb: 4, borderRadius: 2 }}>
        <CardContent>
          {/* Header und Hinzufügen-Button */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="h6">Konfigurierte Kameras</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                {kameras.length} {kameras.length === 1 ? 'Kamera' : 'Kameras'} eingerichtet
              </Typography>
            </Box>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />}
              onClick={() => setAddingKamera(true)}
              sx={{ borderRadius: 8 }}
            >
              Kamera hinzufügen
            </Button>
          </Box>
          
          {/* Formular zum Hinzufügen einer Kamera */}
          {addingKamera && (
            <Card sx={{ 
              p: 3,
              borderRadius: 2,
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              mb: 3
            }}>
              <Typography variant="h6" gutterBottom>Neue Kamera hinzufügen</Typography>
              <Divider sx={{ mb: 3 }} />
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      value={newKamera.displayName}
                      onChange={(e) => setNewKamera({ ...newKamera, displayName: e.target.value })}
                      label="Kamera-Name"
                      variant="outlined"
                      fullWidth
                      placeholder="z.B. Einfahrt Vorne"
                      helperText="Geben Sie der Kamera einen aussagekräftigen Namen"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      value={urlComponents.ip}
                      onChange={(e) => handleComponentChange('ip', e.target.value)}
                      label="IP-Adresse"
                      variant="outlined"
                      fullWidth
                      placeholder="z.B. 192.168.1.100"
                      helperText="IP-Adresse der Kamera im Netzwerk"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      value={urlComponents.username}
                      onChange={(e) => handleComponentChange('username', e.target.value)}
                      label="Benutzername"
                      variant="outlined"
                      fullWidth
                      placeholder="z.B. admin"
                      helperText="Benutzername für die Kamera-Authentifizierung"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      value={urlComponents.password}
                      onChange={(e) => handleComponentChange('password', e.target.value)}
                      label="Passwort"
                      type="password"
                      variant="outlined"
                      fullWidth
                      helperText="Passwort für die Kamera-Authentifizierung"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      value={urlComponents.protocol}
                      onChange={(e) => handleComponentChange('protocol', e.target.value)}
                      label="Protokoll"
                      variant="outlined"
                      fullWidth
                      disabled
                      helperText="Standard: RTSP"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      value={urlComponents.port}
                      onChange={(e) => handleComponentChange('port', e.target.value)}
                      label="Port"
                      variant="outlined"
                      fullWidth
                      helperText="Standard: 554"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      value={urlComponents.path}
                      onChange={(e) => handleComponentChange('path', e.target.value)}
                      label="Pfad"
                      variant="outlined"
                      fullWidth
                      helperText="z.B. /Streaming/Channels/102"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      value={newKamera.url}
                      label="Vollständige RTSP-URL"
                      variant="outlined"
                      fullWidth
                      disabled
                      helperText="Die URL wird automatisch aus den obigen Angaben erstellt"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      value={newKamera.plainUrl || buildPlainUrl(urlComponents)}
                      label="Plain URL (ohne Authentifizierung)"
                      variant="outlined"
                      fullWidth
                      disabled
                      helperText="URL ohne Authentifizierungsdaten"
                    />
                  </Grid>
                </Grid>
                
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setAddingKamera(false)}
                  >
                    Abbrechen
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleConfirmAddKamera}
                    disabled={!isFormValid()}
                  >
                    Kamera hinzufügen
                  </Button>
                </Box>
              </Box>
            </Card>
          )}
          
          {/* Kameras-Grid */}
          {kameras.length === 0 ? (
            <Paper sx={{ 
              p: 4, 
              borderRadius: 2,
              textAlign: 'center',
              backgroundColor: '#f8f9fa'
            }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Keine Kameras konfiguriert
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Fügen Sie eine Kamera hinzu, um mit der Überwachung zu beginnen.
              </Typography>
              <Button 
                variant="contained" 
                startIcon={<AddIcon />}
                onClick={() => setAddingKamera(true)}
              >
                Erste Kamera hinzufügen
              </Button>
            </Paper>
          ) : (
            <Grid container spacing={3}>
              {kameras.map((kamera, index) => (
                <Grid item xs={12} md={12} key={kamera.ref || index}>
                  <KameraItem
                    kamera={kamera}
                    onUpdate={(updatedData) => handleCameraUpdate(index, updatedData)}
                    onDelete={() => handleCameraDelete(index)}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default CameraSettings;